import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Mail, Lock, User, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react'
import { useLogin, useSignup } from '@/lib/api/hooks'
import { apiClient } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errors'
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog'
import { Logo } from '@/components/logo'

// Validation schemas - updated for UUID refactor (no more username)
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
})

const signupSchema = z
  .object({
    name: z.string().min(2, 'Name must be at least 2 characters').optional(),
    email: z.string().email('Please enter a valid email'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type LoginForm = z.infer<typeof loginSchema>
type SignupForm = z.infer<typeof signupSchema>

const AuthPage = () => {
  const [searchParams] = useSearchParams()
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'login'
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode)
  const [error, setError] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false)
  const navigate = useNavigate()

  const loginMutation = useLogin()
  const signupMutation = useSignup()

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const signupForm = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  })

  // Check for OAuth MCP flow
  const oauthRequest = searchParams.get('oauth_request')
  const oauthState = searchParams.get('state')
  const isOAuthFlow = !!oauthRequest

  // Check for password reset success message
  useEffect(() => {
    if (searchParams.get('reset') === 'success') {
      setSuccessMessage(
        'Password updated successfully! You can now sign in with your new password.',
      )
      // Clear the URL param after showing message
      setTimeout(() => {
        window.history.replaceState({}, '', '/login')
      }, 100)
    }

    // If OAuth flow, show message
    if (isOAuthFlow) {
      setSuccessMessage('Sign in to connect your MCP client to EcoSemantic')
    }
  }, [searchParams, isOAuthFlow])

  const onLogin = async (data: LoginForm) => {
    try {
      setError('')
      setSuccessMessage('')

      // Normal login first (to get access token)
      await loginMutation.mutateAsync(data)

      // If OAuth flow, create session token and redirect
      if (isOAuthFlow) {
        // Show connecting message
        setSuccessMessage('✅ Signed in! Connecting to MCP client...')

        // Step 1: Create OAuth session token (secured with Authorization header)
        const sessionResponse = await apiClient.post('/oauth/mcp-session')
        const sessionToken = sessionResponse.data.session_token

        // Step 2: Redirect to completion endpoint with session token
        const params = new URLSearchParams({
          oauth_request: oauthRequest!,
          session_token: sessionToken,
        })
        if (oauthState) {
          params.append('state', oauthState)
        }

        // Browser redirect (no CORS issues, session token is secure & temporary)
        window.location.href = `https://ecosemantic.com/api/v1/oauth/mcp-complete?${params.toString()}`
        return
      }

      // Normal login flow - go to redirect target or dashboard
      const redirectTo = searchParams.get('redirect') || '/dashboard'
      navigate(redirectTo)
    } catch (error) {
      setError(getApiErrorMessage(error, 'Invalid credentials'))
    }
  }

  const onSignup = async (data: SignupForm) => {
    try {
      setError('')
      setSuccessMessage('')
      await signupMutation.mutateAsync({
        email: data.email,
        password: data.password,
        name: data.name,
      })

      // If OAuth flow, automatically log in and complete OAuth
      if (isOAuthFlow) {
        // Show success message with countdown
        setSuccessMessage('✅ Account created! Connecting to MCP client in 3 seconds...')

        // Wait 3 seconds before auto-continuing
        await new Promise((resolve) => setTimeout(resolve, 3000))

        try {
          // Automatically log in the user
          await loginMutation.mutateAsync({
            email: data.email,
            password: data.password,
          })

          // Create OAuth session and redirect
          const sessionResponse = await apiClient.post('/oauth/mcp-session')
          const sessionToken = sessionResponse.data.session_token

          const params = new URLSearchParams({
            oauth_request: oauthRequest!,
            session_token: sessionToken,
          })
          if (oauthState) {
            params.append('state', oauthState)
          }

          window.location.href = `https://ecosemantic.com/api/v1/oauth/mcp-complete?${params.toString()}`
          return
        } catch {
          setError('Signup successful but automatic login failed. Please sign in manually.')
          setMode('login')
          loginForm.setValue('email', data.email)
          return
        }
      }

      // Normal signup flow - show success message with email verification notice
      setMode('login')
      setError('')
      setSuccessMessage(
        `Account created successfully! We've sent a verification email to ${data.email}. ` +
          `Please check your inbox and verify your email address to access all features.`,
      )
      loginForm.setValue('email', data.email)
    } catch (error) {
      // Enhanced error handling for specific cases
      const errorDetail = getApiErrorMessage(error, 'Signup failed')

      // OAuth-specific error handling
      if (isOAuthFlow) {
        // Check if user already exists (generic message from backend)
        if (errorDetail.includes('If this email is available')) {
          setError(
            'This email is already registered. Please use the Login tab to connect your MCP client.',
          )
          setMode('login')
          loginForm.setValue('email', data.email)
          // Don't return - let user login manually
          return
        }
        // Check for deleted account with active subscription
        else if (errorDetail.includes('already have an account')) {
          setError(
            'Your account requires attention. Please check your email for instructions or contact support@ecosemantic.com',
          )
          return
        }
      }

      // Normal error handling (non-OAuth or other errors)
      if (
        errorDetail.includes('active subscription') ||
        errorDetail.includes('already registered')
      ) {
        setError(errorDetail)
      } else if (errorDetail.includes('Email is already registered')) {
        setError('This email is already registered. Please sign in or use a different email.')
      } else {
        setError(errorDetail)
      }
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header with Back Button */}
      <header className="w-full py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => (window.location.href = '/')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className="flex items-center justify-center gap-2 mb-4 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
            >
              <Logo size={40} showText={true} />
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-muted-foreground">
              {mode === 'login'
                ? 'Sign in to access your LCA dashboard'
                : 'Get started with EcoSemantic today'}
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{mode === 'login' ? 'Sign In' : 'Sign Up'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Success Alert */}
              {successMessage && (
                <Alert variant="success">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>{successMessage}</AlertDescription>
                </Alert>
              )}

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Single Form Container with smooth transitions */}
              <motion.div
                key={mode}
                initial={{ opacity: 0, x: mode === 'login' ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: mode === 'login' ? 20 : -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Login Form */}
                {mode === 'login' && (
                  <Form {...loginForm}>
                    <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
                      <FormField
                        control={loginForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  type="email"
                                  placeholder="Enter your email"
                                  className="pl-10"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={loginForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between">
                              <FormLabel>Password</FormLabel>
                              <Button
                                type="button"
                                variant="link"
                                className="p-0 h-auto text-xs text-primary hover:text-primary/80"
                                onClick={() => setForgotPasswordOpen(true)}
                              >
                                Forgot password?
                              </Button>
                            </div>
                            <FormControl>
                              <div className="relative">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  type="password"
                                  placeholder="Enter your password"
                                  className="pl-10"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                        {loginMutation.isPending ? 'Signing In...' : 'Sign In'}
                      </Button>
                    </form>
                  </Form>
                )}

                {/* Signup Form */}
                {mode === 'signup' && (
                  <Form {...signupForm}>
                    <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
                      <FormField
                        control={signupForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <User className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                                <Input placeholder="Your full name" className="pl-10" {...field} />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={signupForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  type="email"
                                  placeholder="your.email@company.com"
                                  className="pl-10"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={signupForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  type="password"
                                  placeholder="Create a password"
                                  className="pl-10"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={signupForm.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Confirm Password</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                                <Input
                                  type="password"
                                  placeholder="Confirm your password"
                                  className="pl-10"
                                  {...field}
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button type="submit" className="w-full" disabled={signupMutation.isPending}>
                        {signupMutation.isPending ? 'Creating Account...' : 'Create Account'}
                      </Button>
                    </form>
                  </Form>
                )}
              </motion.div>

              {/* Mode Toggle - Elegant single line at bottom */}
              <div className="text-center text-sm text-muted-foreground">
                {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
                <Button
                  variant="link"
                  className="p-0 ml-1 text-primary hover:text-primary/80 transition-colors"
                  onClick={() => {
                    setMode(mode === 'login' ? 'signup' : 'login')
                    setError('') // Clear errors when switching
                    setSuccessMessage('') // Clear success message when switching
                  }}
                >
                  {mode === 'login' ? 'Sign up' : 'Sign in'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen} />
    </div>
  )
}

export default AuthPage
